import { useCallback, useMemo } from "react";
import { get, isArray } from "lodash";

import { TaxiService, Company } from "../../services";
import { useTypedSelector } from "../../redux/store";
import { Companies } from "../../redux/reducers/OrdersPage/interface";
import { selectOptionsWrap } from "../../utils";

const useTaxiServiceFilterAccessControl = <
	T,
>(): useTaxiServiceFilterAccessControl.Props<T> => {
	const _taxiServicesFromCurrentUser =
		useTypedSelector(
			(state) => state.account.user?.dispatcherToTaxiServices,
		) || [];

	const lang = useTypedSelector((state) => state.session.language);
	const taxiServices = useTypedSelector((state) => state.taxiServices.models);
	const companies = useTypedSelector(
		(state) => state.ordersPageReducer.companies,
	);

	const taxiServicesFromCurrentUser = useMemo(
		() => _taxiServicesFromCurrentUser,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[_taxiServicesFromCurrentUser.length],
	);

	const idsSetTaxiServices = useMemo(
		() =>
			new Set(
				taxiServicesFromCurrentUser.map(
					(service) => service.taxiService.id || -1,
				),
			),
		[taxiServicesFromCurrentUser],
	);

	const accessTaxiServices: TaxiService.Model[] = useMemo(
		() =>
			taxiServices.filter((taxiService) =>
				idsSetTaxiServices.has(taxiService.id || -2),
			),
		[idsSetTaxiServices, taxiServices],
	);

	const accessCompanies: Company.Model[] = useMemo(() => {
		const retval: Company.Model[] = Array.from(
			new Map(
				accessTaxiServices
					.map((taxiService) => {
						const company = taxiService.company as Company.Model;
						if (!company || company.id === -1) return null;
						return [company.id, company];
					})
					.filter(Boolean) as [number, Company.Model][],
			).values(),
		);
		return retval;
	}, [accessTaxiServices]);

	const accessTaxiServicesIds: number[] = useMemo(() => {
		const fromLoadedModels = accessTaxiServices.map(
			(taxiService) => taxiService.id,
		);
		const ids = fromLoadedModels.length
			? fromLoadedModels
			: taxiServicesFromCurrentUser
					.map((service) => service.taxiService?.id)
					.filter(
						(id): id is number => typeof id === "number" && id > 0,
					);

		return [...ids].sort((left, right) => left - right);
	}, [accessTaxiServices, taxiServicesFromCurrentUser]);

	const accessCompaniesIds: number[] = useMemo(
		() => accessCompanies.map((taxiServices) => taxiServices.id),
		[accessCompanies],
	);

	const accessCompaniesTypeSecond: Companies = useMemo(() => {
		if (!companies?.items) return companies;

		const companiesFilterAccess: Companies = {
			...companies,
			items: (
				companies.items?.filter((item) =>
					accessCompaniesIds.includes(item.id || -1),
				) || []
			).map((item) => ({
				...item,
				taxiServices:
					item.taxiServices?.filter(
						(taxiService: TaxiService.Model) =>
							accessTaxiServicesIds.includes(
								taxiService.id || -1,
							),
					) || [],
			})),
		};
		const retval: Companies = companiesFilterAccess;

		return retval;
	}, [accessCompaniesIds, accessTaxiServicesIds, companies]);

	const idsSetCompanies = useMemo(
		() => new Set(accessCompaniesIds.map((ids) => ids || -1)),
		[accessCompaniesIds],
	);

	const subTaxiServiceFilterAccess = useCallback(
		({
			models,
			branchPath,
			branchPath2,
			isCheckCompanyids,
		}: useTaxiServiceFilterAccessControl.Params<T>): T[] => {
			if (!Array.isArray(models) || typeof branchPath !== "string") {
				return [];
			}

			const arrTypeAccessIds = isCheckCompanyids
				? idsSetCompanies
				: idsSetTaxiServices;

			const accessModels = models.filter((model) => {
				const branchId = get(model, branchPath, -2);

				if (isArray(branchId)) {
					const arrBranchId = branchId as unknown as any[];

					if (arrBranchId.length === 0) {
						return true;
					}

					if (branchPath2) {
						return arrBranchId.some((item) => {
							const nestedId = get(item, branchPath2, -2);
							return (
								typeof nestedId === "number" &&
								arrTypeAccessIds.has(nestedId)
							);
						});
					}

					return (
						arrBranchId.every((id) => typeof id === "number") &&
						arrBranchId.some((id) => arrTypeAccessIds.has(id))
					);
				}

				return arrTypeAccessIds.has(branchId as number);
			});

			return accessModels;
		},
		[idsSetCompanies, idsSetTaxiServices],
	);

	const isNonEmptyNumberArray = (arr: unknown): arr is number[] =>
		Array.isArray(arr) &&
		arr.length > 0 &&
		arr.every((id) => typeof id === "number");

	const subCompaniesOptions = useCallback(
		({
			initialCompanyIds,
			initialTaxiServiceIds,
		}: useTaxiServiceFilterAccessControl.PropsSubCompaniesOptions): selectOptionsWrap.SelectOption[] => {
			const hasOptions = !!(initialCompanyIds || initialTaxiServiceIds);
			let companiesList: Companies["items"] = hasOptions
				? companies?.items || []
				: [];

			if (Array.isArray(companiesList) && hasOptions) {
				if (isNonEmptyNumberArray(initialCompanyIds)) {
					companiesList = companiesList.filter(
						(company: Company.Model) =>
							initialCompanyIds.includes(company.id),
					);
				}

				if (isNonEmptyNumberArray(initialTaxiServiceIds)) {
					companiesList = companiesList.filter((company) =>
						company.taxiServices?.some(
							(taxiService: TaxiService.Model) =>
								initialTaxiServiceIds.includes(
									taxiService.id ?? -1,
								),
						),
					);
				}
			}

			const companiesListOptions: selectOptionsWrap.SelectOption[] =
				companiesList?.map((company) =>
					selectOptionsWrap({
						id: company.id,
						label: company.name?.[lang] ?? "",
					}),
				) || [];

			const accessCompaniesListOptions = Array.isArray(
				accessCompaniesTypeSecond?.items,
			)
				? accessCompaniesTypeSecond.items?.map((company) =>
						selectOptionsWrap({
							id: company.id,
							label: company.name?.[lang] ?? "",
						}),
				  )
				: [];

			return Array.from(
				new Map(
					[
						...accessCompaniesListOptions,
						...companiesListOptions,
					].map((item) => [item.key, item]),
				).values(),
			);
		},
		[companies?.items, accessCompaniesTypeSecond?.items, lang],
	);

	const subTaxiServiceOptions = useCallback(
		({
			initialCompanyIds,
			initialTaxiServiceIds,
			selectedCompanies,
		}: useTaxiServiceFilterAccessControl.PropsSubTaxiServiceOptions): selectOptionsWrap.SelectOption[] => {
			const hasOptions = Boolean(
				initialCompanyIds || initialTaxiServiceIds,
			);

			const filterCompanies = (list: Companies["items"] = []) => {
				if (!Array.isArray(list)) return [];

				const hasCompanyIds =
					hasOptions && isNonEmptyNumberArray(initialCompanyIds);
				const hasSelected = isNonEmptyNumberArray(selectedCompanies);
				const hasServiceIds =
					hasOptions && isNonEmptyNumberArray(initialTaxiServiceIds);

				// includes → O(1)
				const companyIdsSet = hasCompanyIds
					? new Set(initialCompanyIds)
					: null;

				const selectedSet = hasSelected
					? new Set(selectedCompanies)
					: null;

				const serviceIdsSet = hasServiceIds
					? new Set(initialTaxiServiceIds)
					: null;

				return list.reduce<Companies["items"]>((acc, company) => {
					if (companyIdsSet && !companyIdsSet.has(company.id))
						return acc;
					if (selectedSet && !selectedSet.has(company.id)) return acc;

					if (serviceIdsSet) {
						const filteredServices = company.taxiServices?.filter(
							(s) => serviceIdsSet.has(s.id ?? -1),
						);

						if (!filteredServices?.length) return acc;

						acc.push({
							...company,
							taxiServices: filteredServices,
						});
						return acc;
					}

					acc.push(company);
					return acc;
				}, []);
			};

			const mapToOptions = (list: Companies["items"] = []) => {
				if (!list?.length) return [];
				return list.flatMap(
					(company) =>
						company.taxiServices?.map((service) =>
							selectOptionsWrap({
								id: service.id,
								label: service?.settlement?.[lang],
								nameCompany: company.name?.[lang] ?? "",
							}),
						) || [],
				);
			};
			const companiesList = hasOptions
				? filterCompanies(companies?.items)
				: [];
			const filterAccessCompanies = (list: Companies["items"] = []) => {
				if (!Array.isArray(list)) return [];
				const selectedSet = isNonEmptyNumberArray(selectedCompanies)
					? new Set(selectedCompanies)
					: null;
				if (!selectedSet) return list;
				return list.filter((company) => selectedSet.has(company.id));
			};
			const accessCompaniesList = filterAccessCompanies(
				accessCompaniesTypeSecond?.items,
			);

			const allOptions = [
				...mapToOptions(accessCompaniesList),
				...mapToOptions(companiesList),
			];

			const isMultiCompany =
				new Set(allOptions.map((o) => o.nameCompany)).size > 1;

			const result = Array.from(
				new Map(
					allOptions.map((item) => [
						item.key,
						isMultiCompany
							? { ...item, label: item.labelFull }
							: item,
					]),
				).values(),
			);

			return result;
		},
		[companies, accessCompaniesTypeSecond, lang],
	);
	return useMemo(
		() => ({
			subTaxiServiceFilterAccess,
			accessTaxiServices,
			accessCompanies,
			accessTaxiServicesIds,
			accessCompaniesIds,
			accessCompaniesTypeSecond,
			subCompaniesOptions,
			subTaxiServiceOptions,
		}),
		[
			accessCompanies,
			accessCompaniesIds,
			accessCompaniesTypeSecond,
			accessTaxiServices,
			accessTaxiServicesIds,
			subTaxiServiceFilterAccess,
			subCompaniesOptions,
			subTaxiServiceOptions,
		],
	);
};

declare namespace useTaxiServiceFilterAccessControl {
	export interface Params<T> {
		models: T[];
		branchPath: string;
		branchPath2?: string;
		isCheckCompanyids?: boolean;
	}

	export interface PropsSubCompaniesOptions {
		initialCompanyIds?: number[];
		initialTaxiServiceIds?: number[];
	}

	export interface PropsSubTaxiServiceOptions
		extends PropsSubCompaniesOptions {
		selectedCompanies?: number[];
	}

	export interface Props<T> {
		subTaxiServiceFilterAccess: (params: Params<T>) => T[];
		accessTaxiServices: TaxiService.Model[];
		accessCompanies: Company.Model[];
		accessTaxiServicesIds: number[];
		accessCompaniesIds: number[];
		accessCompaniesTypeSecond: Companies;
		subCompaniesOptions: (
			params: PropsSubCompaniesOptions,
		) => selectOptionsWrap.SelectOption[];
		subTaxiServiceOptions: (
			params: PropsSubTaxiServiceOptions,
		) => selectOptionsWrap.SelectOption[];
	}
}

export default useTaxiServiceFilterAccessControl;
